import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";

// Sessão UI-1 — KpiCard primitive heroico.
//
// Composto por:
//   header   → ícone tonalizado + label CAPS tracking-wider (caption color de domínio)
//              + slot opcional pra badge à direita
//   value    → hero number (default 64px, tight leading + letter-spacing) — ReactNode
//              pra permitir text-brand-green / text-status-error condicional sem
//              recriar markup.
//   microcopy → 1 linha curta em text-foreground-muted abaixo do número
//
// Hover: lift -4px + scale 1.02 (mesmo pattern do SummaryCards original).
// Click: navega via onClick (NectarDashboard passa handleSectionChange).
// Sem novos tokens — consome brand-*, domain-*, status-*, foreground-* já definidos.

const TONE_PRESETS = {
  cyan: {
    iconBg: "bg-brand-cyan/10",
    iconColor: "text-brand-cyan",
    labelColor: "text-brand-cyan/80",
    glassToneClass: "",
  },
  amber: {
    iconBg: "bg-status-warning/10",
    iconColor: "text-status-warning",
    labelColor: "text-status-warning/80",
    glassToneClass: "glass-card-amber",
  },
  green: {
    iconBg: "bg-brand-green/10",
    iconColor: "text-brand-green",
    labelColor: "text-brand-green/80",
    glassToneClass: "glass-card-emerald",
  },
  purple: {
    iconBg: "bg-brand-purple/10",
    iconColor: "text-brand-purple",
    labelColor: "text-brand-purple/80",
    glassToneClass: "glass-card-purple",
  },
  error: {
    iconBg: "bg-status-error/10",
    iconColor: "text-status-error",
    labelColor: "text-status-error/80",
    glassToneClass: "",
  },
};

export default function KpiCard({
  icon: Icon,
  label,
  value,
  microcopy,
  badge,
  tone = "cyan",
  valueClassName = "",
  onClick,
  delay = 0,
  testId,
  ariaLabel,
}) {
  const styles = TONE_PRESETS[tone] || TONE_PRESETS.cyan;

  const interactive = typeof onClick === "function";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay }}
      whileHover={interactive ? { y: -4, scale: 1.02 } : undefined}
      whileTap={interactive ? { scale: 0.98 } : undefined}
    >
      <Card
        variant={interactive ? "interactive" : "elevated"}
        onClick={interactive ? onClick : undefined}
        onKeyDown={interactive ? (e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); }
        } : undefined}
        tabIndex={interactive ? 0 : undefined}
        role={interactive ? "button" : undefined}
        aria-label={ariaLabel || label}
        data-testid={testId}
        className={cn(
          "p-6 group glass-card glow-hover",
          styles.glassToneClass,
        )}
      >
        {/* Header: icon + label + badge */}
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            {Icon && (
              <div
                className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center transition-transform duration-300 group-hover:scale-110",
                  styles.iconBg,
                )}
              >
                <Icon className={cn("w-5 h-5", styles.iconColor)} aria-hidden="true" />
              </div>
            )}
            <span
              className={cn(
                "text-caption font-semibold uppercase",
                styles.labelColor,
              )}
              style={{ letterSpacing: "0.08em" }}
            >
              {label}
            </span>
          </div>
          {badge && <div className="shrink-0">{badge}</div>}
        </div>

        {/* Hero value — default 64px, line-height tight, letter-spacing tight.
            Caller pode override via valueClassName (ex: saldo em 56px) sem
            recriar markup. */}
        <div
          className={cn(
            "font-bold text-foreground tabular-nums",
            valueClassName || "text-[64px] leading-none tracking-[-0.02em]",
          )}
        >
          {value}
        </div>

        {/* Microcopy */}
        {microcopy && (
          <p className="text-caption text-foreground-muted mt-3">{microcopy}</p>
        )}
      </Card>
    </motion.div>
  );
}
